/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Page Collective Product component
 *
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.23.0
 */

import { FC, useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { EHttpMethod } from '@/main/modules/app/appConstants';
import { makeSelectTranslationCommon } from '@/main/modules/internationalization/intSelectors';
import { Loader } from '@/main/modules/loader/components/Loader';
import ModalErrorCommon from '@/main/modules/modal/components/ModalError';
import { modalOpen } from '@/main/modules/modal/modalActions';

import { ECollectiveProductFileds } from './constants';
import { useGetCollectiveProductMetaQuery } from './store';
import { CollectiveProductSendForm } from './styled/PageCollectiveProductStyled';

const PageCollectiveProduct: FC = () => {
    const { modal: tModal } = useSelector(makeSelectTranslationCommon());

    const formRef = useRef<HTMLFormElement | null>(null);

    const dispatch = useDispatch();

    const history = useHistory();

    const { data, isFetching, isSuccess, isError } = useGetCollectiveProductMetaQuery();

    const handleError = useCallback(() => {
        dispatch(
            modalOpen({
                onCloseCallback: () => {
                    history.goBack();
                },
                render: ({ onCloseModal }) => <ModalErrorCommon translation={tModal} onCloseModal={onCloseModal} />,
            }),
        );
    }, [dispatch, history, tModal]);

    useEffect(() => {
        if (isSuccess && data && formRef.current) {
            formRef.current.submit();
            history.goBack();
        }
    }, [data, history, isSuccess]);

    useEffect(() => {
        if (isError) {
            handleError();
        }
    }, [handleError, history, isError]);

    return (
        <Loader showLoader={isFetching}>
            {isSuccess && data && (
                <CollectiveProductSendForm
                    action={data.landingUrl}
                    method={EHttpMethod.POST}
                    ref={formRef}
                    target="_blank"
                >
                    <input name={ECollectiveProductFileds.ACCOUNT_ID} value={data.accountId} />
                    <input name={ECollectiveProductFileds.ACCESS_TOKEN} value={data.accessToken} />
                    <input name={ECollectiveProductFileds.REFRESH_TOKEN} value={data.refreshToken} />
                </CollectiveProductSendForm>
            )}
        </Loader>
    );
};

export default PageCollectiveProduct;
